import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Download, Loader2, RefreshCw, Search, Users, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { MultiSelect, MultiSelectOption } from '@/app/components/common/MultiSelect';
import { listSites } from '@/app/services/siteService';
import { listParties, listPartyGroups } from '@/app/services/partyService';
import { listComplianceServices } from '@/app/services/complianceServiceService';
import {
    fetchPartyAnalyticsDetail,
    fetchPartyAnalyticsSummary,
    PartyAnalyticsParams,
    PartyAnalyticsDetail,
    PartyAnalyticsSummaryRow,
} from '@/app/services/supabaseEdgeFunctionService';
import { BUSINESS_TYPES, CLIENT_STATUSES, SOURCE_OPTIONS } from '@/app/types/party';

const CHART_COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

const PartiesReportPage: React.FC = () => {
    // Filter state
    const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>([]);
    const [selectedPartyGroupIds, setSelectedPartyGroupIds] = useState<number[]>([]);
    const [selectedResponsiblePartyIds, setSelectedResponsiblePartyIds] = useState<number[]>([]);
    const [selectedSourcePartyIds, setSelectedSourcePartyIds] = useState<number[]>([]);
    const [selectedSource, setSelectedSource] = useState<string>('all');
    const [selectedBusinessType, setSelectedBusinessType] = useState<string>('all');
    const [selectedClientStatus, setSelectedClientStatus] = useState<string>('all');
    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Build params from filter state
    const filterParams: PartyAnalyticsParams = useMemo(() => ({
        site_ids: selectedSiteIds.length > 0 ? selectedSiteIds : undefined,
        party_group_ids: selectedPartyGroupIds.length > 0 ? selectedPartyGroupIds : undefined,
        responsible_party_ids: selectedResponsiblePartyIds.length > 0 ? selectedResponsiblePartyIds : undefined,
        source_party_ids: selectedSourcePartyIds.length > 0 ? selectedSourcePartyIds : undefined,
        sources: selectedSource !== 'all' ? [selectedSource] : undefined,
        business_types: selectedBusinessType !== 'all' ? [selectedBusinessType] : undefined,
        client_statuses: selectedClientStatus !== 'all' ? [selectedClientStatus] : undefined,
        compliance_service_ids: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
    }), [selectedSiteIds, selectedPartyGroupIds, selectedResponsiblePartyIds, selectedSourcePartyIds, selectedSource, selectedBusinessType, selectedClientStatus, selectedServiceIds]);

    // --- Dropdown data queries ---

    const { data: sitesData = { items: [] }, isLoading: isLoadingSites } = useQuery({
        queryKey: ['sites-for-party-analytics'],
        queryFn: () => listSites({ limit: 100 }),
        staleTime: 5 * 60 * 1000,
    });

    const { data: partyGroupsData = { items: [] }, isLoading: isLoadingPartyGroups } = useQuery({
        queryKey: ['party-groups-for-party-analytics'],
        queryFn: () => listPartyGroups({ limit: 100 }),
        staleTime: 5 * 60 * 1000,
    });

    // Fetch Employee group ID for Responsible Party dropdown
    const { data: employeeGroupData } = useQuery({
        queryKey: ['employee-party-group'],
        queryFn: () => listPartyGroups({
            filters: JSON.stringify([{ field: "name", op: "icontains", value: "Employee" }]),
            limit: 1
        }),
        staleTime: 15 * 60 * 1000,
    });

    const employeeGroupId = employeeGroupData?.items?.[0]?.id;

    const { data: employeePartiesData = { items: [] }, isLoading: isLoadingEmployees } = useQuery({
        queryKey: ['employee-parties-for-analytics', employeeGroupId],
        queryFn: () => listParties({
            limit: 500,
            filters: JSON.stringify([
                { field: "party_group_id", op: "eq", value: employeeGroupId },
            ])
        }),
        enabled: !!employeeGroupId,
        staleTime: 5 * 60 * 1000,
    });

    // Fetch Consultants group ID for Source Consultant dropdown
    const { data: consultantGroupData } = useQuery({
        queryKey: ['consultant-party-group'],
        queryFn: () => listPartyGroups({
            filters: JSON.stringify([{ field: "name", op: "icontains", value: "Consultant" }]),
            limit: 1
        }),
        staleTime: 15 * 60 * 1000,
    });

    const consultantGroupId = consultantGroupData?.items?.[0]?.id;

    const { data: consultantPartiesData = { items: [] }, isLoading: isLoadingConsultants } = useQuery({
        queryKey: ['consultant-parties-for-analytics', consultantGroupId],
        queryFn: () => listParties({
            limit: 500,
            filters: JSON.stringify([
                { field: "party_group_id", op: "eq", value: consultantGroupId },
            ])
        }),
        enabled: !!consultantGroupId,
        staleTime: 5 * 60 * 1000,
    });

    const { data: servicesData = { items: [] }, isLoading: isLoadingServices } = useQuery({
        queryKey: ['compliance-services-for-analytics'],
        queryFn: () => listComplianceServices({ limit: 100, is_active: true }),
        staleTime: 5 * 60 * 1000,
    });

    // --- Report data queries ---

    const { data: detailResponse, isLoading: isLoadingDetail, refetch: refetchDetail } = useQuery({
        queryKey: ['party-analytics-detail', filterParams],
        queryFn: () => fetchPartyAnalyticsDetail(filterParams),
        staleTime: 2 * 60 * 1000,
    });

    const { data: summaryResponse, isLoading: isLoadingSummary, refetch: refetchSummary } = useQuery({
        queryKey: ['party-analytics-summary', filterParams],
        queryFn: () => fetchPartyAnalyticsSummary(filterParams),
        staleTime: 2 * 60 * 1000,
    });

    const isLoading = isLoadingDetail || isLoadingSummary;

    // --- Transform dropdown data ---

    const siteOptions: MultiSelectOption[] = sitesData.items?.map(site => ({
        id: site.id, name: site.name
    })) || [];

    const partyGroupOptions: MultiSelectOption[] = partyGroupsData.items?.map(group => ({
        id: group.id, name: group.name
    })) || [];

    const employeeOptions: MultiSelectOption[] = employeePartiesData.items?.map(party => ({
        id: party.id, name: party.name, extra: party.phone || undefined
    })) || [];

    const consultantOptions: MultiSelectOption[] = consultantPartiesData.items?.map(party => ({
        id: party.id, name: party.name, extra: party.phone || undefined
    })) || [];

    const serviceOptions: MultiSelectOption[] = servicesData.items?.map(svc => ({
        id: svc.id, name: svc.name
    })) || [];

    // --- Process report data ---

    const detailData: PartyAnalyticsDetail[] = detailResponse?.data || [];

    const filteredDetailData = useMemo(() => {
        if (!searchTerm) return detailData;
        const term = searchTerm.toLowerCase();
        return detailData.filter(p =>
            p.party_name.toLowerCase().includes(term) ||
            p.phone?.toLowerCase().includes(term) ||
            p.email?.toLowerCase().includes(term)
        );
    }, [detailData, searchTerm]);

    // Group summary by dimension
    const summaryByDimension = useMemo(() => {
        const grouped: Record<string, PartyAnalyticsSummaryRow[]> = {};
        summaryResponse?.data?.forEach(row => {
            if (!grouped[row.dimension]) grouped[row.dimension] = [];
            grouped[row.dimension].push(row);
        });
        return grouped;
    }, [summaryResponse]);

    // Extract KPI values
    const totalClients = summaryByDimension['kpi']?.find(r => r.dimension_label === 'total_clients')?.party_count || 0;
    const statusData = summaryByDimension['client_status'] || [];
    const activeClients = statusData.find(r => r.dimension_label === 'active')?.party_count || 0;
    const inactiveClients = statusData.filter(r => r.dimension_label !== 'active').reduce((sum, r) => sum + r.party_count, 0);
    const totalServicesSubscribed = (summaryByDimension['service'] || []).reduce((sum, r) => sum + r.party_count, 0);

    // --- Handlers ---

    const handleRefresh = () => {
        refetchDetail();
        refetchSummary();
        toast.success('Refreshing data...');
    };

    const handleExport = () => {
        const csvRows: (string | number)[][] = [];
        csvRows.push(['Client Analytics Report' as string]);
        csvRows.push([`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`]);
        csvRows.push(['']);

        const headers = [
            'Party Name', 'Phone', 'Email', 'GSTIN', 'PAN',
            'Group', 'Site', 'Responsible Party', 'Source Party',
            'Business Type', 'Status', 'Source', 'Services',
            'City', 'State', 'Contact Person'
        ];
        csvRows.push(headers);

        filteredDetailData.forEach(row => {
            csvRows.push([
                `"${row.party_name}"`,
                row.phone || '',
                row.email || '',
                row.gstin || '',
                row.pan || '',
                row.party_group_name || '',
                row.site_name || '',
                row.responsible_party_name || '',
                row.source_party_name || '',
                row.business_type || '',
                row.client_status || '',
                row.source || '',
                `"${row.services_list || ''}"`,
                row.city || '',
                row.state || '',
                row.contact_person || '',
            ]);
        });

        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `client-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Report exported successfully');
    };

    // --- Chart rendering helpers ---

    const renderPieChart = (data: PartyAnalyticsSummaryRow[], title: string) => (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">{title}</CardTitle>
            </CardHeader>
            <CardContent>
                {data.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">No data</div>
                ) : (
                    <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey="party_count"
                                nameKey="dimension_label"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label={({ dimension_label, party_count }) => `${dimension_label} (${party_count})`}
                                labelLine={true}
                            >
                                {data.map((_, index) => (
                                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value: number) => [value, 'Clients']} />
                        </PieChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );

    const renderBarChart = (data: PartyAnalyticsSummaryRow[], title: string) => (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">{title}</CardTitle>
            </CardHeader>
            <CardContent>
                {data.length === 0 ? (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">No data</div>
                ) : (
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={data.slice(0, 10)} layout="vertical" margin={{ left: 10, right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis
                                dataKey="dimension_label"
                                type="category"
                                width={120}
                                tick={{ fontSize: 12 }}
                            />
                            <Tooltip formatter={(value: number) => [value, 'Clients']} />
                            <Bar dataKey="party_count" fill="#10b981" radius={[0, 4, 4, 0]}>
                                {data.slice(0, 10).map((_, index) => (
                                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );

    return (
        <div className="container mx-auto py-6 space-y-6">
            {/* Header & Filters */}
            <Card>
                <CardHeader>
                    <CardTitle>Client Analytics Dashboard</CardTitle>
                    <CardDescription>
                        Analyze clients by site, group, consultant, source, business type, status, and services
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Filter Row 1: MultiSelects */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <Label>Sites</Label>
                            <MultiSelect
                                options={siteOptions}
                                value={selectedSiteIds}
                                onChange={setSelectedSiteIds}
                                placeholder="All sites"
                                searchPlaceholder="Search sites..."
                                loading={isLoadingSites}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Party Groups</Label>
                            <MultiSelect
                                options={partyGroupOptions}
                                value={selectedPartyGroupIds}
                                onChange={setSelectedPartyGroupIds}
                                placeholder="All groups"
                                searchPlaceholder="Search groups..."
                                loading={isLoadingPartyGroups}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Responsible Party</Label>
                            <MultiSelect
                                options={employeeOptions}
                                value={selectedResponsiblePartyIds}
                                onChange={setSelectedResponsiblePartyIds}
                                placeholder="All consultants"
                                searchPlaceholder="Search employees..."
                                loading={isLoadingEmployees}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Source Consultant</Label>
                            <MultiSelect
                                options={consultantOptions}
                                value={selectedSourcePartyIds}
                                onChange={setSelectedSourcePartyIds}
                                placeholder="All source consultants"
                                searchPlaceholder="Search consultants..."
                                loading={isLoadingConsultants}
                            />
                        </div>
                    </div>

                    {/* Filter Row 2: Selects + Services */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <Label>Source</Label>
                            <Select value={selectedSource} onValueChange={setSelectedSource}>
                                <SelectTrigger>
                                    <SelectValue placeholder="All sources" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Sources</SelectItem>
                                    {SOURCE_OPTIONS.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Business Type</Label>
                            <Select value={selectedBusinessType} onValueChange={setSelectedBusinessType}>
                                <SelectTrigger>
                                    <SelectValue placeholder="All types" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Types</SelectItem>
                                    {BUSINESS_TYPES.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Client Status</Label>
                            <Select value={selectedClientStatus} onValueChange={setSelectedClientStatus}>
                                <SelectTrigger>
                                    <SelectValue placeholder="All statuses" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Statuses</SelectItem>
                                    {CLIENT_STATUSES.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Services</Label>
                            <MultiSelect
                                options={serviceOptions}
                                value={selectedServiceIds}
                                onChange={setSelectedServiceIds}
                                placeholder="All services"
                                searchPlaceholder="Search services..."
                                loading={isLoadingServices}
                            />
                        </div>
                    </div>

                    {/* Search and Actions */}
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search by name, phone, email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={handleRefresh} disabled={isLoading}>
                                {isLoading ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                )}
                                Refresh
                            </Button>
                            <Button onClick={handleExport} variant="outline" disabled={isLoading || filteredDetailData.length === 0}>
                                <Download className="mr-2 h-4 w-4" />
                                Export
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-info/5 rounded-lg">
                                <Users className="h-5 w-5 text-info" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Clients</p>
                                <p className="text-xl font-semibold tracking-tight">{totalClients}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-success/5 rounded-lg">
                                <Users className="h-5 w-5 text-success" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Active</p>
                                <p className="text-xl font-semibold tracking-tight text-success">{activeClients}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-destructive/5 rounded-lg">
                                <Users className="h-5 w-5 text-destructive" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Inactive / Suspended</p>
                                <p className="text-xl font-semibold tracking-tight text-destructive">{inactiveClients}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/5 rounded-lg">
                                <FileCheck className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Service Subscriptions</p>
                                <p className="text-xl font-semibold tracking-tight text-primary">{totalServicesSubscribed}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Tabs: Overview / Details */}
            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="details">Details ({filteredDetailData.length})</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4">
                    {isLoadingSummary ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <>
                            {/* Row 1: 2x2 charts */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {renderPieChart(summaryByDimension['party_group'] || [], 'By Party Group')}
                                {renderBarChart(summaryByDimension['site'] || [], 'By Site')}
                                {renderBarChart(summaryByDimension['responsible_party'] || [], 'By Responsible Party')}
                                {renderPieChart(summaryByDimension['business_type'] || [], 'By Business Type')}
                            </div>
                            {/* Row 2: 1x2 charts */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {renderPieChart(summaryByDimension['source'] || [], 'By Source')}
                                {renderBarChart(summaryByDimension['service'] || [], 'By Compliance Service')}
                            </div>
                            {/* Status breakdown */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {renderPieChart(summaryByDimension['client_status'] || [], 'By Client Status')}
                            </div>
                        </>
                    )}
                </TabsContent>

                {/* Details Tab */}
                <TabsContent value="details">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="rounded-md border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Client Name</TableHead>
                                            <TableHead>Phone</TableHead>
                                            <TableHead>Group</TableHead>
                                            <TableHead>Site</TableHead>
                                            <TableHead>Responsible</TableHead>
                                            <TableHead>Business Type</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Source</TableHead>
                                            <TableHead>Services</TableHead>
                                            <TableHead>City</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {isLoadingDetail ? (
                                            <TableRow>
                                                <TableCell colSpan={10} className="text-center py-8">
                                                    <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                                                    <p className="mt-2 text-muted-foreground">Loading client data...</p>
                                                </TableCell>
                                            </TableRow>
                                        ) : filteredDetailData.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                                                    {searchTerm ? 'No clients found matching your search' : 'No clients found'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredDetailData.map((party) => (
                                                <TableRow key={party.party_id}>
                                                    <TableCell className="font-medium">
                                                        <a
                                                            href={`/party/detail/${party.party_id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-primary hover:text-primary hover:underline"
                                                        >
                                                            {party.party_name}
                                                        </a>
                                                    </TableCell>
                                                    <TableCell className="text-sm">{party.phone || '-'}</TableCell>
                                                    <TableCell>
                                                        {party.party_group_name ? (
                                                            <Badge variant="secondary">{party.party_group_name}</Badge>
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell className="text-sm">{party.site_name || '-'}</TableCell>
                                                    <TableCell className="text-sm">{party.responsible_party_name || '-'}</TableCell>
                                                    <TableCell className="text-sm capitalize">{party.business_type?.replace(/_/g, ' ') || '-'}</TableCell>
                                                    <TableCell>
                                                        <Badge variant={
                                                            party.client_status === 'active' ? 'default' :
                                                            party.client_status === 'inactive' ? 'secondary' : 'destructive'
                                                        }>
                                                            {party.client_status || 'active'}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-sm">{party.source || '-'}</TableCell>
                                                    <TableCell className="text-sm max-w-[150px] truncate" title={party.services_list || ''}>
                                                        {party.services_list || '-'}
                                                    </TableCell>
                                                    <TableCell className="text-sm">{party.city || '-'}</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>

                            {filteredDetailData.length > 0 && (
                                <div className="mt-4 text-sm text-muted-foreground text-center">
                                    Showing {filteredDetailData.length}{searchTerm ? ` of ${detailData.length}` : ''} clients
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default PartiesReportPage;
